import React, { useEffect, useState } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  LayoutDashboard, Package, Tags, User, ShoppingCart, FileText, Cloud,
  ArrowLeft, LogOut, BarChart3, Users, Bell, Ticket, ShoppingBag,
  Percent, ChevronLeft, ChevronRight, Disc3, Gift, Newspaper, Truck,
  Menu, X, Scan,
} from "lucide-react";
import { seedFirestoreIfEmpty } from "../../services/seed";

const GROUPS = [
  {
    title: "PANEL",
    items: [
      { to: "/admin", end: true, icon: LayoutDashboard, label: "Ana Panel" },
      { to: "/admin/analytics", icon: BarChart3, label: "Analitika" },
    ],
  },
  {
    title: "KATALOQ",
    items: [
      { to: "/admin/products", icon: Package, label: "Məhsullar" },
      { to: "/admin/categories", icon: Tags, label: "Kateqoriyalar" },
      { to: "/admin/artists", icon: User, label: "Sənətçilər" },
    ],
  },
  {
    title: "SATIŞ",
    items: [
      { to: "/admin/orders", icon: ShoppingCart, label: "Sifarişlər" },
      { to: "/admin/carts", icon: ShoppingBag, label: "Canlı Səbətlər" },
      { to: "/admin/discounts", icon: Percent, label: "Kütləvi Endirim", highlight: true },
      { to: "/admin/coupons", icon: Ticket, label: "Kuponlar" },
      { to: "/admin/gift-cards", icon: Gift, label: "Hədiyyə Kartları", highlight: true },
      { to: "/admin/wheel", icon: Disc3, label: "Şans Çarxı", highlight: true },
      { to: "/admin/spins", icon: Gift, label: "Şans Qazanmaları", highlight: true },
      { to: "/admin/delivery", icon: Truck, label: "Çatdırılma", highlight: true },
    ],
  },
  {
    title: "İSTİFADƏÇİ",
    items: [
      { to: "/admin/customers", icon: Users, label: "Müştərilər" },
      { to: "/admin/notifications", icon: Bell, label: "Bildirişlər" },
    ],
  },
  {
    title: "SİSTEM",
    items: [
      { to: "/admin/vault", icon: Scan, label: "Barkod", highlight: true },
      { to: "/admin/content", icon: FileText, label: "Sayt Mətnləri" },
      { to: "/admin/blog", icon: Newspaper, label: "Yeniliklər" },
      { to: "/admin/buckets", icon: Cloud, label: "Najafli Said" },
    ],
  },
];

export default function AdminLayout() {
  const { profile, logout } = useAuth();
  const location = useLocation();
  const [seeded, setSeeded] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("admin_sidebar_collapsed") === "1"; }
    catch { return false; }
  });

  useEffect(() => {
    seedFirestoreIfEmpty().then(() => setSeeded(true)).catch((e) => console.error("seed failed", e));
  }, []);

  // Auto-close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Prevent body scroll when mobile drawer is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const toggleCollapse = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem("admin_sidebar_collapsed", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  const desktopWidth = collapsed ? "md:w-[68px]" : "md:w-64";

  const NavContent = ({ mobile = false }) => (
    <>
      {/* Brand */}
      <div className="px-4 py-4 md:py-5 border-b border-[var(--border)] flex items-center justify-between gap-2">
        {!collapsed || mobile ? (
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Disc3 size={16} className="text-[var(--accent)] flex-shrink-0" />
              <div className="font-display text-base tracking-tight">VINYL ADMİN</div>
            </div>
            <div className="metadata mt-1 text-[var(--text-muted)] truncate text-[10px]">{profile?.email}</div>
          </div>
        ) : (
          <div className="w-full flex justify-center">
            <Disc3 size={18} className="text-[var(--accent)]" />
          </div>
        )}
        {mobile ? (
          <button
            onClick={() => setMobileOpen(false)}
            className="p-1 text-[var(--text-muted)] hover:text-[var(--primary)] flex-shrink-0 md:hidden"
            aria-label="Bağla"
            data-testid="admin-mobile-close"
          >
            <X size={18} />
          </button>
        ) : (
          <button
            onClick={toggleCollapse}
            className="p-1 text-[var(--text-muted)] hover:text-[var(--primary)] flex-shrink-0 hidden md:block"
            aria-label={collapsed ? "Genişlət" : "Yığ"}
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        )}
      </div>

      {/* Nav groups */}
      <nav className="flex-1 py-3 overflow-y-auto">
        {GROUPS.map((g) => (
          <div key={g.title} className="mb-4">
            {(!collapsed || mobile) && (
              <div className="px-4 pb-2 metadata text-[var(--text-muted)] text-[9px] tracking-[0.18em]">
                {g.title}
              </div>
            )}
            {collapsed && !mobile && <div className="mx-3 my-2 h-px bg-[var(--border)]" />}
            <div className="space-y-px">
              {g.items.map(({ to, end, icon: Icon, label, highlight }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  title={collapsed && !mobile ? label : undefined}
                  className={({ isActive }) =>
                    `relative flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                      isActive
                        ? "bg-[var(--bg)] text-[var(--primary)] font-medium"
                        : "text-[var(--text-2)] hover:bg-[var(--bg)] hover:text-[var(--primary)]"
                    } ${collapsed && !mobile ? "justify-center" : ""}`
                  }
                  data-testid={`admin-nav-${to.replace(/\//g, "-")}`}
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span className="absolute left-0 top-0 bottom-0 w-[2px] bg-[var(--accent)]" />
                      )}
                      <Icon size={15} className={highlight && !isActive ? "text-[var(--accent)]" : ""} />
                      {(!collapsed || mobile) && (
                        <span className="truncate">
                          {label}
                          {highlight && (
                            <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-[var(--accent)] align-middle"/>
                          )}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer actions */}
      <div className="p-3 border-t border-[var(--border)] space-y-1">
        <Link
          to="/"
          title={collapsed && !mobile ? "Mağazaya qayıt" : undefined}
          className={`flex items-center gap-3 px-3 py-2 text-sm text-[var(--text-2)] hover:text-[var(--primary)] ${collapsed && !mobile ? "justify-center" : ""}`}
          data-testid="admin-back-store"
        >
          <ArrowLeft size={15} />
          {(!collapsed || mobile) && <span>Mağazaya qayıt</span>}
        </Link>
        <button
          onClick={logout}
          title={collapsed && !mobile ? "Çıxış" : undefined}
          className={`flex items-center gap-3 px-3 py-2 text-sm text-[var(--text-2)] hover:text-[var(--accent)] w-full ${collapsed && !mobile ? "justify-center" : ""}`}
          data-testid="admin-logout"
        >
          <LogOut size={15} />
          {(!collapsed || mobile) && <span>Çıxış</span>}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] md:flex" data-testid="admin-layout">
      {/* Mobile top bar */}
      <div className="md:hidden sticky top-0 z-40 bg-[var(--surface)] border-b border-[var(--border)] flex items-center justify-between px-4 py-3">
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 -ml-2 text-[var(--text)]"
          aria-label="Menyu"
          data-testid="admin-mobile-toggle"
        >
          <Menu size={22} />
        </button>
        <div className="flex items-center gap-2 font-display text-base">
          <Disc3 size={16} className="text-[var(--accent)]" /> ADMİN
        </div>
        <Link to="/" className="p-2 -mr-2 text-[var(--text-muted)]">
          <ArrowLeft size={18} />
        </Link>
      </div>

      {/* Desktop sidebar */}
      <aside className={`${desktopWidth} border-r border-[var(--border)] flex-col bg-[var(--surface)] sticky top-0 h-screen overflow-y-auto transition-[width] duration-200 hidden md:flex`}>
        <NavContent mobile={false} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 z-50 bg-black/60"
            onClick={() => setMobileOpen(false)}
            data-testid="admin-mobile-backdrop"
          />
          <aside className="md:hidden fixed left-0 top-0 bottom-0 z-50 w-72 max-w-[85vw] bg-[var(--surface)] border-r border-[var(--border)] flex flex-col shadow-xl">
            <NavContent mobile={true} />
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <Outlet context={{ seeded }} />
      </main>
    </div>
  );
}
